import { observer } from 'mobx-react';

import { LegalEntityStatus, LegalEntityType } from 'chaika-enums';

import styles from './BasePriceItem.module.scss';

import { PriceModelData } from '@store/models/Price';

import { OptionType, Select } from '@components/Select';
import { LegalEntityAsyncMultiSelect } from '@components/ModelSelect/LegalEntrySelect';
import { Icon, IconType } from '@components/Icon';
import { MoneyInput } from '@components/Input';
import isEmpty from 'lodash/isEmpty';
import { DatePicker } from '@components/datePickers';
import { Checkbox } from '@components/Checkbox';
import { useHandlers } from './hooks/useHandlers';
import { translate } from '@chaika/i18n/helper';

interface Props {
    index: number;
    disabled: boolean;
    item: PriceModelData;
    currencyOptions: OptionType<string>[];
    contractorOptions: OptionType<string>[];
    dataTest: string;
    onChange(item: Partial<PriceModelData>): void;
    onDelete(): void;
}

export const BasePriceItem = observer(
    ({ item, onDelete, index, dataTest, currencyOptions, contractorOptions, onChange, disabled }: Props) => {
        const { money, onSelectChange, onInputDateChange, onInputMoneyChange, onToggle, onBlurMoney } = useHandlers({
            item,
            onChange,
        });

        return (
            <div className={styles.root}>
                <div className={styles.title}>
                    <div>
                        {translate('basePrice')} {index + 1}
                    </div>
                    {!disabled && (
                        <div
                            className={styles.delete}
                            onClick={onDelete}
                        >
                            <Icon type={IconType.TRASH_REDESIGN} />
                        </div>
                    )}
                </div>
                <div className={styles.fields}>
                    <LegalEntityAsyncMultiSelect
                        disabled={disabled}
                        dataTest={`${dataTest}_legalEntitySelect`}
                        title={translate('legalEntities')}
                        types={[LegalEntityType.Clinic]}
                        statuses={[LegalEntityStatus.Active]}
                        value={item.legalEntityIds ?? []}
                        onChange={ids => onChange({ legalEntityIds: ids })}
                    />
                    <Select
                        disabled={disabled}
                        hideSelectedOptions
                        name={'contractorId'}
                        dataTest={`${dataTest}_contractorSelect`}
                        title={translate('contractor')}
                        selectedValue={item.contractorId}
                        options={contractorOptions ?? []}
                        onChange={onSelectChange}
                    />
                    <div className={styles.pairFields}>
                        <div className={styles.price}>
                            <MoneyInput
                                disabled={disabled}
                                withoutCurrency
                                title={translate('price')}
                                name={'value'}
                                dataTest={`${dataTest}_money`}
                                value={money}
                                onBlur={onBlurMoney}
                                onChange={onInputMoneyChange}
                            />
                        </div>
                        <div className={styles.currency}>
                            <Select
                                disabled={disabled}
                                hideSelectedOptions
                                dataTest={`${dataTest}_currency`}
                                name={'currency'}
                                title={translate('currency')}
                                selectedValue={item.currency}
                                options={currencyOptions}
                                onChange={onSelectChange}
                            />
                        </div>
                    </div>
                    <div className={styles.date}>
                        <DatePicker
                            disabled={disabled}
                            dataTest={`${dataTest}_currency`}
                            title={translate('activationDate')}
                            name={'scheduledOn'}
                            placeholder={translate('ddmmyyyy')}
                            selected={!isEmpty(item.scheduledOn) ? new Date(item.scheduledOn) : null}
                            onChange={onInputDateChange}
                        />
                    </div>
                    <Checkbox
                        disabled={disabled}
                        label={translate('compositeService')}
                        isChecked={item.isComposite ?? false}
                        dataTest={`${dataTest}_isComposite`}
                        onChange={() => onToggle(item.isComposite ?? false, 'isComposite')}
                    />
                </div>
            </div>
        );
    },
);
